import React, { useState } from 'react';
import './contuct.css';
import { 
  Mail, 
  Phone, 
  MessageSquare, 
  MapPin, 
  Linkedin, 
  Github, 
  Twitter, 
  Send, 
  User, 
  Zap,
  CheckCircle,
  AlertCircle 
} from 'lucide-react';
import Particle from '../Particle';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [lastSubmit, setLastSubmit] = useState(null);
  const RATE_LIMIT = 60000; // 1 minute

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Email is invalid';
    if (!formData.subject.trim()) return 'Subject is required';
    if (!formData.message.trim()) return 'Message is required';
    return null;
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  // In your Contact.js component
  const sendEmail = async (e) => {
    e.preventDefault();
    setIsLoading(!isLoading);



    // Check rate limit
    if (lastSubmit && Date.now() - lastSubmit < RATE_LIMIT) {
      showAlert('Please wait before sending another message.', 'error');
      setIsLoading(!isLoading);
      return;
    }
    
    setLastSubmit(Date.now());

    validateForm();
    
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      subject: formData.subject,
      message: formData.message,
      to_name: 'Your Name',
      sent_date: new Date().toLocaleDateString(),
      sent_time: new Date().toLocaleTimeString(),
    };

    const emailjs = await import('emailjs-com');

    try {
      await emailjs.send(
        'service_w040hpl',     // Replace with your Service ID
        'template_d4qyaw9',    // Replace with your Template ID
        templateParams,
        'zGbkQ4oMqIT5voXOl'      // Replace with your Public Key
      );
      
      showAlert('Message sent successfully! I\'ll get back to you soon.', 'success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsLoading(!isLoading);
    } catch (error) {
      console.error('EmailJS Error:', error);
      showAlert('Failed to send message. Please try again.', 'error');
      setIsLoading(!isLoading);
    }
  };

  const contactInfo = [
    {
      icon: <Phone size={20} />,
      label: 'Phone',
      value: '+254 710 416 091',
      href: 'tel:+254710416091'
    },
    {
      icon: <MessageSquare size={20} />,
      label: 'WhatsApp',
      value: '+254 710 416 091',
      href: 'https://wa.me/254710416091'
    },
    {
      icon: <Mail size={20} />,
      label: 'Email',
      value: 'shadilikkle@gmail.com',
      href: 'mailto:shadilikkle@gmail.com'
    },
    {
      icon: <Mail size={20} />,
      label: 'Alt Email',
      value: 'shadikali599@gmail.com',
      href: 'mailto:shadikali599@gmail.com'
    },
    {
      icon: <MapPin size={20} />,
      label: 'Location',
      value: 'Nairobi, Edloret, Kenya',
      href: null
    }
  ];

  const socialLinks = [
    {
      icon: <Linkedin size={20} />,
      href: '#',
      name: 'LinkedIn'
    },
    {
      icon: <Github size={20} />,
      href: 'https://github.com/Sheldon-codes',
      name: 'GitHub'
    },
    {
      icon: <Twitter size={20} />,
      href: '#',
      name: 'Twitter'
    }
  ];

  return (
    <div className="contact-section">
      <Particle/>
      
      <div className="contact-container">
        <div className="contact-content">
          {/* Header */}
          <div className="contact-header">
            <h1 className="contact-title">
              Get In <span className="purple">Touch</span>
            </h1>
            <p className="contact-subtitle">
              Have a project in mind? Let's work together to bring your ideas to life.
              I'm always excited to discuss new opportunities and collaborations.
            </p>
          </div>

          {/* Alert */}
          {alert.show && (
            <div className={`alert alert-${alert.type}`}>
              {alert.type === 'success' ? (
                <CheckCircle size={20} className="alert-icon" />
              ) : (
                <AlertCircle size={20} className="alert-icon" />
              )}
              {alert.message}
            </div>
          )}

          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h3 className="form-title">
                <Send size={20} className="title-icon" />
                Send Me a Message
              </h3>
              
              <div className="contact-form" onSubmit={sendEmail}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <User size={16} className="form-icon" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">
                      <Mail size={16} className="form-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    <Mail size={16} className="form-icon" />
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="form-control"
                    placeholder="What's this about?"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <Send size={16} className="form-icon" />
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-control textarea"
                    rows="6"
                    placeholder="Tell me about your project or just say hello..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>

                <button 
                  type="button" 
                  className={`send-button ${isLoading ? 'loading' : ''}`}
                  onClick={sendEmail}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="button-icon" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              {/* <h3 className="info-title">Let's Connect</h3>
              <p className="info-description">
                Feel free to reach out through any of these channels. 
                I typically respond within 24 hours.
              </p> */}

              <div className="contact-methods">
                {contactInfo.map((item, index) => (
                  <div key={index} className="contact-method">
                    <div className="method-icon">
                      {item.icon}
                    </div>
                    <div className="method-info">
                      <span className="method-label">{item.label}</span>
                      {item.href ? (
                        <a 
                          href={item.href} 
                          className="method-value"
                          target={item.href.startsWith('http') ? '_blank' : '_self'}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : ''}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="method-value">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="social-section">
                <h4 className="social-title">Follow Me</h4>
                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      className="social-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      title={social.name}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="response-promise">
                <div className="promise-icon">
                  <Zap size={24} />
                </div>
                <div className="promise-text">
                  <strong>Quick Response Guaranteed</strong>
                  <p>I'll get back to you within 24 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default Contact;